<template>
  <div class="p-3 w-full flex flex-col gap-2 m-auto h-[700px]">
    <Toolbar>
      <template #center>
        <div class="flex flex-row gap-8">
          <div v-for="filter in filters" :key="filter.name" class="flex items-center">
            <RadioButton v-model="selectedFilter" :inputId="filter.name" name="dynamic" :value="filter.name" @change="onFilterChanged" />
            <label :for="filter.name" class="ml-2">{{ filter.name }} ({{ filter.amount }})</label>
          </div>
        </div>
      </template>
      <template #end>
        <IconButton v-tooltip.top="'Change Columns'" icon="cog" outlined small @click="customizeSubdivisionColumns = true"> </IconButton>
      </template>
    </Toolbar>
    <div class="w-full flex-auto">
      <ag-grid-vue
        theme="legacy"
        :rowData="filteredProjects"
        :columnDefs="colDefs"
        :defaultColDef="defaultColDefs"
        :autoSizeStrategy="{ type: 'fitCellContents' }"
        :rowClassRules="rowClassRules"
        :suppressCellFocus="true"
        class="mx-4 py-3 h-full"
        :class="darkVal ? 'ag-theme-quartz-dark' : 'ag-theme-quartz'"
        rowSelection="single"
        :pinnedBottomRowData="footers"
        :isExternalFilterPresent="isExternalFilterPresent"
        :doesExternalFilterPass="doesExternalFilterPass"
        @row-data-updated="resize"
        @grid-size-changed="resize"
        @row-double-clicked="projectSelected"
        @grid-ready="(event: GridReadyEvent) => gridApi = event.api"
      >
      </ag-grid-vue>
    </div>
  </div>
  <TSDialog label="Customize Subdivision Columns" v-model:visible="customizeSubdivisionColumns" icon="cog" size="w-[900px]!">
    <template #content>
      <CustomizableColumnManager
        modal
        :initial-rows="subdivision.subdivisionColumns"
        :scope="SettingScope.SUBDIVISION_COLUMN_TEMPLATES"
        @confirm="subdivisionColumnsCustomized"
      ></CustomizableColumnManager>
    </template>
  </TSDialog>
</template>

<script setup lang="ts">
import { AgGridVue } from "ag-grid-vue3";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";

import { usePropsStore } from "@/stores/props";
import type { Subdivision, SubdivisionDrawTotal } from "@/types/Subdivision";
import { computed, onMounted, ref } from "vue";
import { currencyFormatter, dateFormatter, percentFormatter, testBit } from "@/services/Utility";
import Toolbar from "primevue/toolbar";
import { ProjectStatusCategory } from "@/enums/ProjectStatusCategory";
import type { GridApi, GridReadyEvent, RowClassParams, RowDoubleClickedEvent } from "ag-grid-community";
import type { CustomizableColumn } from "@/types/CustomizableColumns";
import { SubdivisionColumns } from "@/enums/SubdivisionColumns";
import CustomizableColumnManager from "../CustomizableColumnManager.vue";
import { SettingScope } from "@/enums/SystemSettings";

const selectedFilter = ref<"Pending" | "Active" | "Sold" | "All">("Active");

interface Filter {
  name: string;
  amount: number;
}

const filters = ref<Filter[]>([
  { name: "All", amount: 0 },
  { name: "Pending", amount: 0 },
  { name: "Active", amount: 0 },
  { name: "Sold", amount: 0 },
]);

interface Props {
  drawTotals: SubdivisionDrawTotal[];
}

const subdivision = defineModel<Subdivision>("subdivision", { required: true });

const gridApi = ref<GridApi>();
const props = defineProps<Props>();
const propsStore = usePropsStore();
const darkVal = computed<boolean>(() => propsStore.darkMode);
const filteredProjects = ref<SubdivisionDrawTotal[]>(props.drawTotals);
const customizeSubdivisionColumns = ref<boolean>(false);

const footers = ref([
  {
    projectName: null,
    projectCode: "TOTAL",
    drawNumber: null,
    propertyType: null,
    startDate: null,
    completionPosted: null,
    extensions: null,
    totalCreditCommitment: 0,
    totalEquity: 0,
    totalAdjustedBudget: 0,
    retailValue: 0,
    salesPrice: 0,
    totalThisDraw: 0,
    totalDrawnToDate: 0,
    totalFundsAvailable: 0,
    totalBudgetDrawn: null,
    totalSiteComplete: null,
    totalHardCosts: 0,
    totalSoftCosts: 0,
    totalLand: 0,
    totalInterest: 0,
    totalInspection: 0,
    releaseAmount: 0,
    releaseDate: null,
    initialBudget: 0,
    budgetAmount: 0,
    cumulativeAdjustment: 0,
    priorDrawnToDate: 0,
    totalThisLoan: 0,
    totalThisEquity: 0,
    netThisDraw: 0,
    priorEquityToDate: 0,
    projectedOver: 0,
    loanToDate: 0,
    netThisDrawEquity: 0,
    equityToDate: 0,
    variance: 0,
    totalFundsRequired: 0,
    totalRetainagePriorDraw: 0,
    totalReleasedPriorDraw: 0,
    totalRetainage: 0,
    totalReleased: 0,
    retainageBalance: 0,
  },
]);

onMounted(async (): Promise<void> => {
  calculateTotals();
  updateTotals();
  showColumns();
  rearrangeColumns();
});

const resize = (): void => {
  gridApi.value?.autoSizeColumns([]);
};

const rowClassRules = ref({
  "font-bold": (params: RowClassParams) => {
    return params.node.rowPinned != null;
  },
});

const defaultColDefs = {
  filter: true,
  floatingFilter: false,
  cellDataType: "text",
  minWidth: 100,
};

const isExternalFilterPresent = (): boolean => {
  return selectedFilter.value !== "All";
};

const doesExternalFilterPass = (node: any): boolean => {
  const total: SubdivisionDrawTotal = node.data;

  switch (selectedFilter.value) {
    case "Pending":
      return total.projectStatus.category === ProjectStatusCategory.PENDING;
    case "Active":
      return total.projectStatus.category === ProjectStatusCategory.ACTIVE;
    case "Sold":
      return total.projectStatus.category === ProjectStatusCategory.SOLD;
    default:
      return true;
  }
};

const onFilterChanged = (): void => {
  gridApi.value?.onFilterChanged();
  updateTotals();
};

const calculateTotals = (): void => {
  let active = 0;
  let pending = 0;
  let sold = 0;

  props.drawTotals.forEach((project: SubdivisionDrawTotal) => {
    if (project.projectStatus.category === ProjectStatusCategory.PENDING) {
      pending++;
    } else if (project.projectStatus.category === ProjectStatusCategory.ACTIVE) {
      active++;
    } else if (project.projectStatus.category === ProjectStatusCategory.SOLD) {
      sold++;
    }
  });

  filters.value.forEach((value) => {
    if (value.name === "Pending") value.amount = pending;
    else if (value.name === "Active") value.amount = active;
    else if (value.name === "Sold") value.amount = sold;
    else if (props.drawTotals) value.amount = props.drawTotals.length;
  });
};

const addToTotal = (category: string, item: SubdivisionDrawTotal, amount: number | null): number => {
  if (amount == null) return 0;
  return category === "Total" || category.toUpperCase() === item.projectStatus.name.toUpperCase() ? amount : 0;
};

const rearrangeColumns = () => {
  const order: string[] = [];
  subdivision.value.subdivisionColumnOrder.forEach((col) => {
    const entry = Object.entries(SubdivisionColumns).find((entry) => entry[1].bit === col);
    if (entry == null) return;
    order.push(entry[0]);
  });
  colDefs.value = colDefs.value.sort((a, b) => order.indexOf(a.field ?? "") - order.indexOf(b.field ?? ""));
  gridApi.value?.setGridOption("columnDefs", colDefs.value);
};

const subdivisionColumnsCustomized = (columns: number): void => {
  subdivision.value.subdivisionColumns = columns;
  showColumns();
  customizeSubdivisionColumns.value = false;
};

const showColumns = () => {
  const visible: string[] = [];
  const invisible: string[] = [];
  Object.entries(SubdivisionColumns).forEach((entry) => {
    if (entry[1].alwaysVisible) return;
    if (subdivision.value.subdivisionColumns == null || testBit(subdivision.value.subdivisionColumns, entry[1].bit)) visible.push(entry[0]);
    else invisible.push(entry[0]);
  });
  gridApi.value?.setColumnsVisible(visible, true);
  gridApi.value?.setColumnsVisible(invisible, false);
};

const getColumnOrder = (): number[] => {
  if (gridApi.value == null) return [];
  return gridApi.value.getColumnState().map((col: any) => {
    let fieldKey = col.colId as keyof typeof SubdivisionColumns;
    return SubdivisionColumns[fieldKey].bit;
  });
};

defineExpose({ getColumnOrder });

const router = useRouter();
const projectSelected = (event: RowDoubleClickedEvent): void => {
  router.push({
    name: "project-dashboard",
    params: {
      id: event.data.projectId,
    },
  });
};

const updateTotals = (): void => {
  footers.value = [
    {
      projectName: null,
      projectCode: selectedFilter.value === "All" ? "Total" : selectedFilter.value,
      drawNumber: null,
      propertyType: null,
      startDate: null,
      completionPosted: null,
      extensions: null,
      totalCreditCommitment: 0,
      totalEquity: 0,
      totalAdjustedBudget: 0,
      retailValue: 0,
      salesPrice: 0,
      totalThisDraw: 0,
      totalDrawnToDate: 0,
      totalFundsAvailable: 0,
      totalBudgetDrawn: null,
      totalSiteComplete: null,
      totalHardCosts: 0,
      totalSoftCosts: 0,
      totalLand: 0,
      totalInterest: 0,
      totalInspection: 0,
      releaseAmount: 0,
      releaseDate: null,
      initialBudget: 0,
      budgetAmount: 0,
      cumulativeAdjustment: 0,
      priorDrawnToDate: 0,
      totalThisLoan: 0,
      totalThisEquity: 0,
      netThisDraw: 0,
      priorEquityToDate: 0,
      projectedOver: 0,
      loanToDate: 0,
      netThisDrawEquity: 0,
      equityToDate: 0,
      variance: 0,
      totalFundsRequired: 0,
      totalRetainagePriorDraw: 0,
      totalReleasedPriorDraw: 0,
      totalRetainage: 0,
      totalReleased: 0,
      retainageBalance: 0,
    },
  ];

  const footer = footers.value[0];
  const category = footer.projectCode;
  footer.totalCreditCommitment = props.drawTotals.reduce((acc, curr) => acc + addToTotal(category, curr, curr.totalCreditCommitment), 0);
  footer.totalEquity = props.drawTotals.reduce((acc, curr) => acc + addToTotal(category, curr, curr.totalEquity), 0);
  footer.totalAdjustedBudget = props.drawTotals.reduce((acc, curr) => acc + addToTotal(category, curr, curr.totalAdjustedBudget), 0);
  footer.retailValue = props.drawTotals.reduce((acc, curr) => acc + addToTotal(category, curr, curr.retailValue), 0);
  footer.salesPrice = props.drawTotals.reduce((acc, curr) => acc + addToTotal(category, curr, curr.salesPrice), 0);
  footer.totalThisDraw = props.drawTotals.reduce((acc, curr) => acc + addToTotal(category, curr, curr.totalThisDraw), 0);
  footer.totalDrawnToDate = props.drawTotals.reduce((acc, curr) => acc + addToTotal(category, curr, curr.totalDrawnToDate), 0);
  footer.totalFundsAvailable = props.drawTotals.reduce((acc, curr) => acc + addToTotal(category, curr, curr.totalFundsAvailable), 0);
  footer.totalHardCosts = props.drawTotals.reduce((acc, curr) => acc + addToTotal(category, curr, curr.totalHardCosts), 0);
  footer.totalSoftCosts = props.drawTotals.reduce((acc, curr) => acc + addToTotal(category, curr, curr.totalSoftCosts), 0);
  footer.totalLand = props.drawTotals.reduce((acc, curr) => acc + addToTotal(category, curr, curr.totalLand), 0);
  footer.totalInterest = props.drawTotals.reduce((acc, curr) => acc + addToTotal(category, curr, curr.totalInterest), 0);
  footer.totalInspection = props.drawTotals.reduce((acc, curr) => acc + addToTotal(category, curr, curr.totalInspection), 0);
  footer.releaseAmount = props.drawTotals.reduce((acc, curr) => acc + addToTotal(category, curr, curr.releaseAmount), 0);
  footer.initialBudget = props.drawTotals.reduce((acc, curr) => acc + addToTotal(category, curr, curr.initialBudget), 0);
  footer.budgetAmount = props.drawTotals.reduce((acc, curr) => acc + addToTotal(category, curr, curr.budgetAmount), 0);
  footer.cumulativeAdjustment = props.drawTotals.reduce((acc, curr) => acc + addToTotal(category, curr, curr.cumulativeAdjustment), 0);
  footer.priorDrawnToDate = props.drawTotals.reduce((acc, curr) => acc + addToTotal(category, curr, curr.priorDrawnToDate), 0);
  footer.totalThisLoan = props.drawTotals.reduce((acc, curr) => acc + addToTotal(category, curr, curr.totalThisLoan), 0);
  footer.totalThisEquity = props.drawTotals.reduce((acc, curr) => acc + addToTotal(category, curr, curr.totalThisEquity), 0);
  footer.netThisDraw = props.drawTotals.reduce((acc, curr) => acc + addToTotal(category, curr, curr.netThisDraw), 0);
  footer.priorEquityToDate = props.drawTotals.reduce((acc, curr) => acc + addToTotal(category, curr, curr.priorEquityToDate), 0);
  footer.projectedOver = props.drawTotals.reduce((acc, curr) => acc + addToTotal(category, curr, curr.projectedOver), 0);
  footer.loanToDate = props.drawTotals.reduce((acc, curr) => acc + addToTotal(category, curr, curr.loanToDate), 0);
  footer.netThisDrawEquity = props.drawTotals.reduce((acc, curr) => acc + addToTotal(category, curr, curr.netThisDrawEquity), 0);
  footer.equityToDate = props.drawTotals.reduce((acc, curr) => acc + addToTotal(category, curr, curr.equityToDate), 0);
  footer.variance = props.drawTotals.reduce((acc, curr) => acc + addToTotal(category, curr, curr.variance), 0);
  footer.totalFundsRequired = props.drawTotals.reduce((acc, curr) => acc + addToTotal(category, curr, curr.totalFundsRequired), 0);
  footer.totalRetainagePriorDraw = props.drawTotals.reduce((acc, curr) => acc + addToTotal(category, curr, curr.totalRetainagePriorDraw), 0);
  footer.totalReleasedPriorDraw = props.drawTotals.reduce((acc, curr) => acc + addToTotal(category, curr, curr.totalReleasedPriorDraw), 0);
  footer.totalRetainage = props.drawTotals.reduce((acc, curr) => acc + addToTotal(category, curr, curr.totalRetainage), 0);
  footer.totalReleased = props.drawTotals.reduce((acc, curr) => acc + addToTotal(category, curr, curr.totalReleased), 0);
  footer.retainageBalance = props.drawTotals.reduce((acc, curr) => acc + addToTotal(category, curr, curr.retainageBalance), 0);
  gridApi.value?.setGridOption("pinnedBottomRowData", footers.value);
};

const hideColumn = (column: CustomizableColumn): boolean => {
  return subdivision.value.subdivisionColumns == null ? false : !testBit(subdivision.value.subdivisionColumns, column.bit);
};

const colDefs = ref([
  {
    field: "projectName",
    headerName: "Project Name",
    pinned: true,
  },
  {
    field: "projectCode",
    headerName: "Loan #",
    pinned: true,
  },
  {
    cellDataType: "number",
    field: "drawNumber",
    headerName: "Draw Number",
    initialHide: hideColumn(SubdivisionColumns.drawNumber),
  },
  {
    field: "propertyType",
    headerName: "Property Type",
    initialHide: hideColumn(SubdivisionColumns.propertyType),
  },
  {
    field: "dateCreated",
    headerName: "Start Date",
    valueFormatter: dateFormatter,
    initialHide: hideColumn(SubdivisionColumns.dateCreated),
  },
  {
    field: "completionPosted",
    headerName: "Completion Posted",
    valueFormatter: dateFormatter,
    initialHide: hideColumn(SubdivisionColumns.completionPosted),
  },
  {
    field: "extensions",
    cellDataType: "number",
    headerName: "Extensions",
    initialHide: hideColumn(SubdivisionColumns.extensions),
  },
  {
    field: "totalCreditCommitment",
    cellDataType: "number",
    cellClass: "text-end",
    headerName: "Credit Commitment",
    headerClass: "grid-align-center",
    valueFormatter: currencyFormatter,
    initialHide: hideColumn(SubdivisionColumns.totalCreditCommitment),
  },
  {
    cellDataType: "number",
    cellClass: "text-end",
    field: "totalEquity",
    headerName: "Equity Balance",
    headerClass: "grid-align-center",
    valueFormatter: currencyFormatter,
    initialHide: hideColumn(SubdivisionColumns.totalEquity),
  },
  {
    cellDataType: "number",
    cellClass: "text-end",
    field: "totalAdjustedBudget",
    headerName: "Total Cost",
    headerClass: "grid-align-center",
    valueFormatter: currencyFormatter,
    initialHide: hideColumn(SubdivisionColumns.totalAdjustedBudget),
  },
  {
    cellDataType: "number",
    cellClass: "text-end",
    field: "retailValue",
    headerName: "Retail Value",
    headerClass: "grid-align-center",
    valueFormatter: currencyFormatter,
    initialHide: hideColumn(SubdivisionColumns.retailValue),
  },
  {
    cellDataType: "number",
    cellClass: "text-end",
    field: "salesPrice",
    headerName: "Sales Price",
    headerClass: "grid-align-center",
    valueFormatter: currencyFormatter,
    initialHide: hideColumn(SubdivisionColumns.salesPrice),
  },
  {
    cellDataType: "number",
    cellClass: "text-end",
    field: "totalThisDraw",
    headerName: "This Draw",
    headerClass: "grid-align-center",
    valueFormatter: currencyFormatter,
    initialHide: hideColumn(SubdivisionColumns.totalThisDraw),
  },
  {
    cellDataType: "number",
    cellClass: "text-end",
    field: "totalDrawnToDate",
    headerName: "Drawn To Date",
    headerClass: "grid-align-center",
    valueFormatter: currencyFormatter,
    initialHide: hideColumn(SubdivisionColumns.totalDrawnToDate),
  },
  {
    cellDataType: "number",
    cellClass: "text-end",
    field: "totalFundsAvailable",
    headerName: "Funds Available",
    headerClass: "grid-align-center",
    valueFormatter: currencyFormatter,
    initialHide: hideColumn(SubdivisionColumns.totalFundsAvailable),
  },
  {
    cellDataType: "number",
    cellClass: "text-end",
    field: "totalBudgetDrawn",
    headerName: "% Drawn",
    headerClass: "grid-align-center",
    valueFormatter: percentFormatter,
    initialHide: hideColumn(SubdivisionColumns.totalBudgetDrawn),
  },
  {
    cellDataType: "number",
    cellClass: "text-end",
    field: "totalSiteComplete",
    headerName: "% Inspected",
    headerClass: "grid-align-center",
    valueFormatter: percentFormatter,
    initialHide: hideColumn(SubdivisionColumns.totalSiteComplete),
  },
  {
    cellDataType: "number",
    cellClass: "text-end",
    field: "totalHardCosts",
    headerName: "Hard Cost",
    headerClass: "grid-align-center",
    valueFormatter: currencyFormatter,
    initialHide: hideColumn(SubdivisionColumns.totalHardCosts),
  },
  {
    cellDataType: "number",
    cellClass: "text-end",
    field: "totalSoftCosts",
    headerName: "Soft Cost",
    headerClass: "grid-align-center",
    valueFormatter: currencyFormatter,
    initialHide: hideColumn(SubdivisionColumns.totalSoftCosts),
  },
  {
    cellDataType: "number",
    cellClass: "text-end",
    field: "totalLand",
    headerName: "Land",
    headerClass: "grid-align-center",
    valueFormatter: currencyFormatter,
    initialHide: hideColumn(SubdivisionColumns.totalLand),
  },
  {
    cellDataType: "number",
    cellClass: "text-end",
    field: "totalInterest",
    headerName: "Interest",
    headerClass: "grid-align-center",
    valueFormatter: currencyFormatter,
    initialHide: hideColumn(SubdivisionColumns.totalInterest),
  },
  {
    cellDataType: "number",
    cellClass: "text-end",
    field: "totalInspection",
    headerName: "Inspection",
    headerClass: "grid-align-center",
    valueFormatter: currencyFormatter,
    initialHide: hideColumn(SubdivisionColumns.totalInspection),
  },
  {
    cellDataType: "number",
    cellClass: "text-end",
    field: "releaseAmount",
    headerName: "Release Amount",
    headerClass: "grid-align-center",
    valueFormatter: currencyFormatter,
    initialHide: hideColumn(SubdivisionColumns.releaseAmount),
  },
  {
    field: "releaseDate",
    headerName: "Release Date",
    valueFormatter: dateFormatter,
    initialHide: hideColumn(SubdivisionColumns.releaseDate),
  },
  {
    field: "initialBudget",
    cellDataType: "number",
    headerName: "Initial Budget",
    headerClass: "text-center",
    valueFormatter: currencyFormatter,
    initialHide: hideColumn(SubdivisionColumns.initialBudget),
  },
  {
    field: "budgetAmount",
    cellDataType: "number",
    headerName: "Budget Amount",
    valueFormatter: currencyFormatter,
    initialHide: hideColumn(SubdivisionColumns.budgetAmount),
  },
  {
    field: "cumulativeAdjustment",
    cellDataType: "number",
    headerName: "Cumulative Adjustment",
    headerClass: "text-center",
    valueFormatter: currencyFormatter,
    initialHide: hideColumn(SubdivisionColumns.cumulativeAdjustment),
  },
  {
    field: "priorDrawnToDate",
    cellDataType: "number",
    headerName: "Prior Drawn To Date",
    valueFormatter: currencyFormatter,
    initialHide: hideColumn(SubdivisionColumns.priorDrawnToDate),
  },
  {
    field: "totalThisLoan",
    cellDataType: "number",
    headerName: "This Draw Loan",
    valueFormatter: currencyFormatter,
    initialHide: hideColumn(SubdivisionColumns.totalThisLoan),
  },
  {
    field: "totalThisEquity",
    cellDataType: "number",
    headerName: "This Draw Equity",
    initialHide: hideColumn(SubdivisionColumns.totalThisEquity),
  },
  {
    field: "netThisDraw",
    cellDataType: "number",
    headerName: "Net This Draw Loan",
    valueFormatter: currencyFormatter,
    initialHide: hideColumn(SubdivisionColumns.netThisDraw),
  },
  {
    field: "priorEquityToDate",
    cellDataType: "number",
    headerName: "Prior Equity To Date",
    valueFormatter: currencyFormatter,
    initialHide: hideColumn(SubdivisionColumns.priorEquityToDate),
  },
  {
    field: "projectedOver",
    cellDataType: "number",
    headerName: "Projected Over / (Under)",
    valueFormatter: currencyFormatter,
    initialHide: hideColumn(SubdivisionColumns.projectedOver),
  },
  {
    field: "loanToDate",
    cellDataType: "number",
    headerName: "Loan Drawn To Date",
    valueFormatter: currencyFormatter,
    initialHide: hideColumn(SubdivisionColumns.loanToDate),
  },
  {
    field: "netThisDrawEquity",
    cellDataType: "number",
    headerName: "Net This Draw Equity",
    valueFormatter: currencyFormatter,
    initialHide: hideColumn(SubdivisionColumns.netThisDrawEquity),
  },
  {
    field: "equityToDate",
    cellDataType: "number",
    headerName: "Equity Drawn To Date",
    valueFormatter: currencyFormatter,
    initialHide: hideColumn(SubdivisionColumns.equityToDate),
  },
  {
    field: "variance",
    cellDataType: "number",
    headerName: "Variance",
    valueFormatter: currencyFormatter,
    initialHide: hideColumn(SubdivisionColumns.variance),
  },
  {
    field: "totalFundsRequired",
    cellDataType: "number",
    headerName: "Funds Required",
    valueFormatter: currencyFormatter,
    initialHide: hideColumn(SubdivisionColumns.totalFundsRequired),
  },
  {
    field: "totalRetainagePriorDraw",
    cellDataType: "number",
    headerName: "Total Retainage Prior Draws",
    valueFormatter: currencyFormatter,
    initialHide: hideColumn(SubdivisionColumns.totalRetainagePriorDraw),
  },
  {
    field: "totalReleasedPriorDraw",
    cellDataType: "number",
    headerName: "Total Released Prior Draws",
    valueFormatter: currencyFormatter,
    initialHide: hideColumn(SubdivisionColumns.totalReleasedPriorDraw),
  },
  {
    field: "totalRetainage",
    cellDataType: "number",
    headerName: "Total Retainage",
    valueFormatter: currencyFormatter,
    initialHide: hideColumn(SubdivisionColumns.totalRetainage),
  },

  {
    field: "totalReleased",
    cellDataType: "number",
    headerName: "Total Released",
    valueFormatter: currencyFormatter,
    initialHide: hideColumn(SubdivisionColumns.totalReleased),
  },
  {
    field: "retainageBalance",
    cellDataType: "number",
    headerName: "Retainage Balance",
    valueFormatter: currencyFormatter,
    initialHide: hideColumn(SubdivisionColumns.retainageBalance),
  },
]);
</script>
